// Das Boot mit der Truppe an Bord (B19, B25, Erste Fassung, Runde 7): die leeren Boote aus public/art/boot/ mit den
// Tieren je Spezies und Pose (Manifest, kind crew) auf ihren Plätzen. Ebenen nach den Hinweisen des Grafik-Leads: im
// Querschnitt das Boot, die Tiere nach z, dann quer_vorn; von außen das Boot, die Tiere draußen, die Tiere hinter den
// Fenstern nur innerhalb der Fenstermaske, dann aussen_vorn (darin liegt das Glas). Boot, Tiere und vorn-Bilder schaukeln
// gemeinsam und tragen dasselbe Licht der Tageszeit (bootTon). Dazu die kleinen Momente: Zzz in der Koje, Sprechblasen im
// Gemeinschaftsraum, der Ausruf des Ausgucks, Dampf beim Heiler. Wird auch für die Ankunft am Meer gebraucht.
// Runde 8: Das Boot schaukelt (rock.ts). Boot, Fenstermaske und vorn-Bilder werden in Streifen gleichen Versatzes
// gezeichnet, jedes Tier rückt als Ganzes mit dem Punkt, auf dem es steht, samt seinen Zeichen.
import { fxFrame, fxSeconds, image, lookImage, sprite, type ArtSprite } from '../art.ts';
import { img, type Boot } from './data.ts';
import type { Aboard } from './crew.ts';
import type { Rock } from './rock.ts';

/** Rand um das Boot in den Ebenen, damit das Schaukeln nichts abschneidet */
export const PAD = 8;

/** Wo die Figuren stehen: Rechtecke in Pixeln der Leinwand, für die Maus */
export type Hit = { key: string; x: number; y: number; w: number; h: number };

export type BoatDraw = {
  boat: Boot;
  view: 'quer' | 'aussen';
  crew: Aboard[];
  /** oben links auf der Leinwand, mit dem Schaukeln */
  x: number;
  y: number;
  /** Licht der Tageszeit (bootTon) */
  tone: string;
  now: number;
  /** Die Sprechblasen im Gemeinschaftsraum (zwei quatschen) */
  chat?: boolean;
  /** Der Ausguck hat etwas entdeckt */
  look?: boolean;
  /** Runde 8: Schaukeln; ohne liegt das Boot still */
  rock?: Rock;
};

/** Versatz eines Punkts im Bild des Boots durch das Schaukeln, ganze Pixel */
export const rockAt = (rock: Rock | undefined, x: number, y: number): [number, number] => (rock ? [rock.dx(y), rock.dy(x)] : [0, 0]);

/** Ein Bild des Boots geschaukelt zeichnen: Spalten- und Zeilenstreifen einzeln, oben links bei (ox, oy) */
export function drawRocked(g: CanvasRenderingContext2D, src: CanvasImageSource, w: number, h: number, ox: number, oy: number, rock: Rock | undefined): void {
  if (!rock) {
    g.drawImage(src, 0, 0, w, h, ox, oy, w, h);
    return;
  }
  for (const [x0, x1, dy] of rock.cols)
    for (const [y0, y1, dx] of rock.rows) {
      const cw = Math.min(x1, w) - x0;
      const ch = Math.min(y1, h) - y0;
      if (cw > 0 && ch > 0) g.drawImage(src, x0, y0, cw, ch, ox + x0 + dx, oy + y0 + dy, cw, ch);
    }
}

const canvases = new Map<string, HTMLCanvasElement>();
function scratch(name: string, w: number, h: number): CanvasRenderingContext2D {
  let c = canvases.get(name);
  if (!c) {
    c = document.createElement('canvas');
    canvases.set(name, c);
  }
  if (c.width !== w || c.height !== h) {
    c.width = w;
    c.height = h;
  } else c.getContext('2d')!.clearRect(0, 0, w, h);
  const g = c.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  g.globalAlpha = 1;
  g.globalCompositeOperation = 'source-over';
  return g;
}

/** Kleiner Streuwert je Tier, damit nicht alle im Gleichtakt atmen (Hinweis: an zufälliger Stelle beginnen) */
function phaseOf(key: string): number {
  let h = 7;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) % 10007;
  return (h % 997) / 97;
}

/** Oberste Zeile mit Farbe in einem Bild eines Streifens, zwischengespeichert */
const tops = new Map<string, number>();
function topRow(src: HTMLImageElement, frame: number, fw: number, fh: number): number {
  const key = `${src.src}|${frame}`;
  const done = tops.get(key);
  if (done !== undefined) return done;
  const g = scratch('top', fw, fh);
  g.drawImage(src, frame * fw, 0, fw, fh, 0, 0, fw, fh);
  const px = g.getImageData(0, 0, fw, fh).data;
  let top = 0;
  outer: for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) if (px[(y * fw + x) * 4 + 3]! > 40) {
    top = y;
    break outer;
  }
  tops.set(key, top);
  return top;
}

/** Ein Bild eines Streifens so setzen, dass sein Punkt (px, py) auf (X, Y) liegt; flip spiegelt um diesen Punkt */
function put(g: CanvasRenderingContext2D, src: CanvasImageSource, frame: number, fw: number, fh: number, X: number, Y: number, px: number, py: number, flip: boolean): void {
  const ax = flip ? fw - 1 - px : px;
  const dx = Math.round(X - ax);
  const dy = Math.round(Y - py);
  if (!flip) {
    g.drawImage(src, frame * fw, 0, fw, fh, dx, dy, fw, fh);
    return;
  }
  g.save();
  g.translate(dx + fw, dy);
  g.scale(-1, 1);
  g.drawImage(src, frame * fw, 0, fw, fh, 0, 0, fw, fh);
  g.restore();
}

/** Ein Effekt der Bord-Momente (Streifen mit sequence) mit seinem center auf (X, Y) */
function effect(g: CanvasRenderingContext2D, id: string, age: number, X: number, Y: number, flip = false): void {
  const sp = sprite('effect', id);
  const src = sp ? image(sp.file) : null;
  if (!sp || !src) return;
  const f = fxFrame(sp, age);
  if (f === null) return;
  const [fw, fh] = sp.frameSize ?? [src.naturalWidth, src.naturalHeight];
  const c = sp.center ?? [Math.floor(fw / 2), fh - 1];
  put(g, src, f, fw, fh, X, Y, c[0], c[1], flip);
}

const crewSprite = (a: Aboard): ArtSprite | undefined => sprite('crew', `${a.species}-${a.pose}`);

/** Die Tiere einer Sicht in eine Ebene zeichnen; Rückgabe: wo sie stehen (in Pixeln des Boots) */
function drawAnimals(g: CanvasRenderingContext2D, list: Aboard[], s: BoatDraw, out: Hit[]): void {
  const chatters = list.filter((a) => a.pose === 'sitzen').sort((a, b) => (a.seat ?? 0) - (b.seat ?? 0));
  const patients = s.crew.some((a) => a.role === 'kajuete');
  for (const a of [...list].sort((p, q) => p.place.z - q.place.z)) {
    const sp = crewSprite(a);
    const file = sp?.file;
    const base = file ? image(file) : null;
    const anchorAt = a.place.posen[a.pose];
    if (!sp || !base || !anchorAt || !file) continue;
    const src = lookImage(file, a.look, sprite('species', a.species)) ?? base;
    // Runde 8: Das Tier rückt als Ganzes mit dem Punkt des Boots, auf dem es steht
    const [ox, oy] = rockAt(s.rock, anchorAt[0], anchorAt[1]);
    const at: [number, number] = [anchorAt[0] + ox + PAD, anchorAt[1] + oy + PAD];
    const [fw, fh] = sp.frameSize ?? [base.naturalWidth, base.naturalHeight];
    const [ax, ay] = sp.anchor ?? [Math.floor(fw / 2), fh - 1];
    const flip = a.place.blick === 'links';
    // Im Gemeinschaftsraum wechseln sich zwei mit Reden und Zuhören ab (Hinweis „sitzen“: 2,4 s versetzt)
    const seat = chatters.indexOf(a);
    const age = seat >= 0 ? s.now + 50 - (seat % 2) * 2.4 : s.now + phaseOf(a.key);
    let frame = fxFrame(sp, age) ?? 0;
    if (a.pose === 'ausguck' && s.look) frame = 4;
    put(g, src, frame, fw, fh, at[0], at[1], ax, ay, flip);
    const dx = Math.round(at[0] - (flip ? fw - 1 - ax : ax));
    const dy = Math.round(at[1] - ay);
    out.push({ key: a.key, x: dx - PAD, y: dy - PAD, w: fw, h: fh });
    const mirrorX = (px: number) => (flip ? fw - 1 - px : px);
    // Kleine Momente an Bord
    if (a.pose === 'koje') {
      const z = (sp.points?.zzz as [number, number] | undefined) ?? [Math.round(fw * 0.6), -3];
      effect(g, 'zzz', s.now + phaseOf(a.key), dx + mirrorX(z[0]), dy + z[1]);
    } else if (a.pose === 'sitzen' && s.chat && seat >= 0 && seat < 2 && chatters.length >= 2) {
      // Die Spitze der Blase auf die oberste Zeile minus 1, bei zwei Dritteln der Breite (nach links: einem Drittel)
      const top = topRow(base, frame, fw, fh);
      effect(g, 'bord-sprechblase', age, dx + Math.round(flip ? fw / 3 : (fw * 2) / 3), dy + top - 1, flip);
    } else if (a.pose === 'ausguck' && s.look) {
      const top = topRow(base, frame, fw, fh);
      effect(g, 'bord-ausruf', s.now % fxSeconds(sprite('effect', 'bord-ausruf')), at[0], dy + top - 1, flip);
    } else if (a.pose === 'heilen' && patients) {
      effect(g, 'bord-dampf', s.now + phaseOf(a.key), at[0] + (flip ? -12 : 12), at[1] - 10, flip);
    }
  }
}

/**
 * Das Boot einer Sicht mit allen Ebenen, noch ohne Licht, mit dem Rand PAD rundherum; Rückgabe die Ebene und wo die
 * Tiere stehen (in Pixeln des Boots)
 */
function compose(s: BoatDraw, view: 'quer' | 'aussen', hits: Hit[]): HTMLCanvasElement | null {
  const b = s.boat;
  const body = img(`boot/${b.dateien[view]}`);
  const front = img(`boot/${b.dateien[`${view}_vorn`]}`);
  if (!body || !front) return null;
  // Pinne: Steht niemand am Steuer, ist sie hochgeholt (Hinweis „Pinne“)
  const resting = b.pinne_ruhe && !s.crew.some((a) => a.role === 'kaeptn') ? img(`boot/${b.pinne_ruhe.datei}`) : null;
  let hull: CanvasImageSource = body;
  if (resting && b.pinne_ruhe) {
    const h = scratch(`boat-hull-${view}`, b.w, b.h);
    const c = b.pinne_ruhe.spalten;
    h.drawImage(body, c, 0, b.w - c, b.h, c, 0, b.w - c, b.h);
    h.drawImage(resting, 0, 0);
    hull = h.canvas;
  }
  const g = scratch(`boat-${view}`, b.w + 2 * PAD, b.h + 2 * PAD);
  drawRocked(g, hull, b.w, b.h, PAD, PAD, s.rock);
  if (view === 'quer') drawAnimals(g, s.crew.filter((a) => a.place.sicht.includes('quer')), s, hits);
  else {
    drawAnimals(g, s.crew.filter((a) => a.place.sicht.includes('aussen')), s, hits);
    // Hinter den Fenstern: nur innerhalb der Fenstermaske, die mit dem Boot schaukelt
    const inside = s.crew.filter((a) => a.place.sicht.includes('fenster') && !a.place.sicht.includes('aussen'));
    const mask = img(`boot/${b.dateien.fenster}`);
    if (inside.length && mask) {
      const w = scratch('boat-window', b.w + 2 * PAD, b.h + 2 * PAD);
      drawAnimals(w, inside, s, hits);
      w.globalCompositeOperation = 'destination-in';
      drawRocked(w, mask, b.w, b.h, PAD, PAD, s.rock);
      g.drawImage(w.canvas, 0, 0);
    }
  }
  drawRocked(g, front, b.w, b.h, PAD, PAD, s.rock);
  return g.canvas;
}

/** Eine Ebene mit dem Licht der Tageszeit multiplizieren, die Deckkraft bleibt */
function tinted(src: HTMLCanvasElement, tone: string, name: string): HTMLCanvasElement {
  const g = scratch(name, src.width, src.height);
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'multiply';
  g.fillStyle = tone;
  g.fillRect(0, 0, src.width, src.height);
  g.globalCompositeOperation = 'destination-in';
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'source-over';
  return g.canvas;
}

/**
 * Das Boot zeichnen: die Sicht, dazu die Außenansicht für die Spiegelung (Rückgabe), und die Tiere als Treffer für die
 * Maus (in Pixeln der Leinwand). Die Ebenen haben rundherum den Rand PAD; gezeichnet wird so, dass das Boot bei (x, y) liegt
 */
export function drawBoat(ctx: CanvasRenderingContext2D, s: BoatDraw, hits: Hit[] = []): { view: HTMLCanvasElement; outside: HTMLCanvasElement } | null {
  const own: Hit[] = [];
  const outsideRaw = compose(s, 'aussen', s.view === 'aussen' ? own : []);
  if (!outsideRaw) return null;
  const outside = tinted(outsideRaw, s.tone, 'boat-tone-aussen');
  let view = outside;
  if (s.view === 'quer') {
    const raw = compose(s, 'quer', own);
    if (!raw) return null;
    view = tinted(raw, s.tone, 'boat-tone-quer');
  }
  ctx.drawImage(view, Math.round(s.x) - PAD, Math.round(s.y) - PAD);
  for (const h of own) hits.push({ ...h, x: h.x + Math.round(s.x), y: h.y + Math.round(s.y) });
  return { view, outside };
}

/** Dunkle Silhouette über einer Ebene (Unwetter, Nacht): nur wo sie deckt; (x, y) ist ihre linke obere Ecke */
export function shadeBoat(ctx: CanvasRenderingContext2D, layer: HTMLCanvasElement, x: number, y: number, color: string, alpha: number): void {
  if (alpha <= 0) return;
  const g = scratch('boat-shade', layer.width, layer.height);
  g.drawImage(layer, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = color;
  g.fillRect(0, 0, layer.width, layer.height);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(g.canvas, Math.round(x), Math.round(y));
  ctx.restore();
}
